import { useState } from 'react';
import { useDeleteDdPhase } from '@/api/hooks';
import { ApiError, ConfirmDialog, useToast } from '@dataroom/shared';
import type { DdPhaseSummary } from '@dataroom/shared';

type ItemChoice = 'move' | 'delete';

/**
 * Delete a phase. An empty phase just goes; a phase that holds items first asks
 * what becomes of them — move them to another phase, or delete them with it. The
 * phase's questions (and any answers) are removed with it, and the dialog says so.
 */
export function DeletePhaseDialog({
  roomId,
  phase,
  otherPhases,
  onClose,
}: {
  roomId: string;
  phase: DdPhaseSummary;
  /** Every other real phase of the room, in order — the places items can go. */
  otherPhases: DdPhaseSummary[];
  onClose: () => void;
}) {
  const del = useDeleteDdPhase(roomId);
  const toast = useToast();
  const items = phase.total;
  const [choice, setChoice] = useState<ItemChoice | null>(items > 0 ? null : 'delete');
  const [target, setTarget] = useState(otherPhases[0]?.phaseId ?? '');
  const name = phase.name ?? 'this phase';
  const noun = items === 1 ? 'item' : 'items';

  const ready = items === 0 || choice === 'delete' || (choice === 'move' && target !== '');
  const confirmLabel =
    items === 0
      ? 'Delete phase'
      : choice === 'move'
        ? 'Move items and delete phase'
        : `Delete phase and ${items} ${noun}`;

  return (
    <ConfirmDialog
      danger
      title={`Delete “${name}”?`}
      confirmLabel={confirmLabel}
      pending={del.isPending}
      confirmDisabled={!ready}
      onCancel={onClose}
      onConfirm={() =>
        phase.phaseId &&
        del.mutate(
          {
            phaseId: phase.phaseId,
            items:
              items === 0
                ? undefined
                : choice === 'move'
                  ? { action: 'move', toPhaseId: target }
                  : { action: 'delete' },
          },
          {
            onSuccess: () => {
              toast({ message: `Deleted the phase “${name}”` });
              onClose();
            },
          },
        )
      }
    >
      {items === 0 ? (
        <p>This phase has no items. Any questions you added to it are removed with it.</p>
      ) : (
        <fieldset className="space-y-2">
          <legend className="mb-1">
            This phase has {items} {noun}. What should happen to them?
          </legend>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="phase-items"
              className="mt-1"
              checked={choice === 'move'}
              disabled={otherPhases.length === 0}
              onChange={() => setChoice('move')}
            />
            <span>
              Move the {items} {noun} to another phase
              {otherPhases.length === 0 ? (
                <span className="block text-xs text-ink-3">There is no other phase to move them to.</span>
              ) : (
                <select
                  aria-label="Move the items to"
                  className="mt-1 block rounded border border-input bg-background px-2 py-1 text-sm"
                  value={target}
                  disabled={choice !== 'move'}
                  onChange={(e) => setTarget(e.target.value)}
                >
                  {otherPhases.map((p) => (
                    <option key={p.phaseId} value={p.phaseId ?? ''}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="phase-items"
              className="mt-1"
              checked={choice === 'delete'}
              onChange={() => setChoice('delete')}
            />
            <span>
              Delete the {items} {noun} too
              <span className="block text-xs text-ink-3">
                Documents linked to them stay in the data room; the links are removed.
              </span>
            </span>
          </label>
          <p className="text-xs text-ink-3">
            Questions in this phase, and any answers the seller gave, are removed with it.
          </p>
        </fieldset>
      )}
      {del.error instanceof ApiError ? (
        <p role="alert" className="text-sm text-destructive">
          {del.error.message}
        </p>
      ) : null}
    </ConfirmDialog>
  );
}
